import {
  Droplets,
  Clock,
  Smartphone,
  Activity,
  FlaskConical,
  CheckCircle2,
  Heart
} from "lucide-react";

const STEPS = [
  { step: "01", title: "Collect Sample", description: "Add 1 drop of urine to each well on the KidneyCheQ device.", icon: "droplet" },
  { step: "02", title: "Wait 90 Seconds", description: "Allow the reagents to react with your urine sample.", icon: "timer" },
  { step: "03", title: "Scan with App", description: "Open the KidneyCheQ app and scan the device using your smartphone camera.", icon: "scan" },
  { step: "04", title: "Get Results", description: "AI analyzes the color reaction and displays your kidney health status instantly.", icon: "chart" }
];

const getStepIcon = (iconType) => {
  switch (iconType) {
    case "droplet":
      return (
        <div className="relative">
          <Droplets className="w-10 h-10 text-amber-400" />
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded bg-slate-700 flex items-center justify-center">
            <FlaskConical className="w-3 h-3 text-cyan-600" />
          </div>
        </div>
      );
    case "timer":
      return (
        <div className="relative">
          <Clock className="w-10 h-10 text-cyan-600" />
          <span className="absolute -bottom-1 -right-1 text-xs font-bold text-emerald-600 bg-slate-100 px-1 rounded">90s</span>
        </div>
      );
    case "scan":
      return <Smartphone className="w-10 h-10 text-indigo-600" />;
    case "chart":
      return <Activity className="w-10 h-10 text-emerald-600" />;
    default:
      return null;
  }
};

export const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 md:py-32 overflow-hidden">
    <div className="absolute inset-0 bg-white" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(6,182,212,0.08),transparent_70%)]" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="text-cyan-600 text-sm font-mono uppercase tracking-widest">Simple Process</span>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-800 mt-4 mb-6">
          How Does <span className="gradient-text">KidneyCheQ</span> Work?
        </h2>
        <p className="text-slate-600 text-lg">
          Four simple steps to check your kidney health at home
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {STEPS.map((item, index) => (
          <div key={item.step} data-testid={`step-card-${item.step}`} className="relative group">
            {index < STEPS.length - 1 && (
              <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-slate-700 to-transparent z-0" />
            )}

            <div className="relative glass rounded-2xl p-6 h-full">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 border border-cyan-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                {getStepIcon(item.icon)}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-cyan-600 font-mono text-sm">{item.step}</span>
                <h3 className="font-display text-xl font-semibold text-slate-800">{item.title}</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Product Instructions Image */}
      <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="glass rounded-2xl p-6">
          <img
            src="https://customer-assets.emergentagent.com/job_ominar-hub/artifacts/s37bqqgm_WhatsApp%20Image%202026-02-14%20at%2019.22.24.jpeg"
            alt="KidneyCheQ Instructions - How to Use"
            className="w-full h-auto rounded-xl shadow-lg"
            data-testid="kidneycheq-instructions-image"
          />
        </div>
        <div className="space-y-6">
          <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-800">What's Inside the Kit</h3>
          <ul className="space-y-3">
            {["Test Device", "Urine Container", "Urine Dropper", "Solution B"].map((item) => (
              <li key={item} className="flex items-center gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
            <p className="text-cyan-600 text-sm font-medium">
              Download the KidneyCheQ App from Google Play Store or App Store to scan and get your results!
            </p>
          </div>
        </div>
      </div>

      {/* Usage Frequency */}
      <div className="mt-20 glass rounded-2xl p-8 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-800 mb-4">Frequency of Usage</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Regular monitoring helps detect kidney issues early, when they're most treatable.
            </p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-100/50 border border-slate-300">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-slate-800 font-semibold">At-Risk Individuals</p>
                <p className="text-slate-600 text-sm">Every 3 months</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-100/50 border border-slate-300">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Activity className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-slate-800 font-semibold">Health-Conscious</p>
                <p className="text-slate-600 text-sm">At least twice a year</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
